import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook for auto-logout with activity detection
 * 
 * Features:
 * - 60-minute inactivity timeout
 * - Warning modal 2-3 minutes before logout
 * - Tracks mouse, keyboard, scroll, navigation
 * - Prevents logout during active quiz, video, or form filling
 * 
 * @param {Function} onLogout - Callback when logout should occur
 * @param {Function} onWarning - Callback when warning should be shown (receives timeRemaining in ms)
 * @param {Object} options - Configuration options
 */
export const useAutoLogout = (
  onLogout,
  onWarning,
  options = {}
) => {
  const {
    timeoutMinutes = 60,           // Total inactivity timeout
    warningMinutes = 2,             // Warning shown X minutes before logout
    checkInterval = 1000,           // Check every second
  } = options;

  const location = useLocation();
  const lastActivityRef = useRef(Date.now());
  const warningShownRef = useRef(false);
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const isActiveRef = useRef(true);
  const logoutTriggeredRef = useRef(false); // Prevent hook from interfering once logout is triggered
  const checkIntervalRef = useRef(null); // Store interval ID to stop it when needed

  // Check if we're in a special state where logout should be prevented
  const shouldPreventLogout = useCallback(() => {
    // Check if we're on a quiz page (active quiz attempt)
    if (location.pathname.includes('/quizzes/') || location.pathname.includes('/quizattempts/')) {
      // Check if there's an active quiz (form with answers or quiz container)
      const quizForm = document.querySelector('form, [data-quiz-active="true"], [class*="quiz"]');
      const quizContainer = document.querySelector('[class*="quiz"], [id*="quiz"]');
      if (quizForm || quizContainer) {
        return true;
      }
      // Also check if there are answer inputs or buttons
      const answerInputs = document.querySelectorAll('input[type="text"], input[type="number"], textarea, button[class*="answer"]');
      if (answerInputs.length > 0) {
        return true;
      }
    }

    // Check if video is playing
    const videos = document.querySelectorAll('video');
    for (const video of videos) {
      if (!video.paused && !video.ended) {
        return true;
      }
    }

    // Check if form is being filled (has focus or recent input)
    const activeElement = document.activeElement;
    if (
      activeElement &&
      (activeElement.tagName === 'INPUT' ||
       activeElement.tagName === 'TEXTAREA' ||
       activeElement.tagName === 'SELECT' ||
       activeElement.isContentEditable)
    ) {
      return true;
    }

    // Check for any form with recent changes (within last 30 seconds)
    const forms = document.querySelectorAll('form');
    for (const form of forms) {
      if (form.dataset.recentActivity === 'true') {
        return true;
      }
    }

    return false;
  }, [location.pathname]);

  // Reset activity timer
  const resetActivity = useCallback(() => {
    // Don't reset if logout has been triggered
    if (logoutTriggeredRef.current) {
      return;
    }

    const now = Date.now();
    lastActivityRef.current = now;
    warningShownRef.current = false;
    isActiveRef.current = true;

    // Clear existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
  }, []);

  // Handle activity detection
  const handleActivity = useCallback(() => {
    // Don't handle activity if logout has been triggered
    if (logoutTriggeredRef.current) {
      return;
    }
    if (!shouldPreventLogout()) {
      resetActivity();
    }
  }, [shouldPreventLogout, resetActivity]);

  // Check inactivity and trigger warnings/logout
  const checkInactivity = useCallback(() => {
    // Stop checking if logout has been triggered OR warning has been shown
    if (logoutTriggeredRef.current || warningShownRef.current) {
      return; // ✅ Stop checking once warning is shown - let modal handle it
    }

    if (shouldPreventLogout()) {
      // Don't count inactivity if in special state - reset timer
      lastActivityRef.current = Date.now();
      warningShownRef.current = false;
      isActiveRef.current = true;
      return;
    }

    const now = Date.now();
    const timeSinceActivity = now - lastActivityRef.current;
    const timeoutMs = timeoutMinutes * 60 * 1000;
    const warningMs = warningMinutes * 60 * 1000;
    const timeUntilWarning = timeoutMs - warningMs;
    const timeUntilLogout = timeoutMs - timeSinceActivity;

    // Show warning if we're past the warning threshold but haven't shown it yet
    if (timeSinceActivity >= timeUntilWarning && !warningShownRef.current) {
      warningShownRef.current = true;
      if (onWarning) {
        onWarning(timeUntilLogout);
      }
      // ✅ Don't check for logout anymore - modal will handle it
      return;
    }

    // This should never be reached now, but keep as safety
    if (timeSinceActivity >= timeoutMs) {
      if (onLogout && !logoutTriggeredRef.current) {
        logoutTriggeredRef.current = true;
        onLogout();
      }
    }
  }, [timeoutMinutes, warningMinutes, shouldPreventLogout, onLogout, onWarning]);

  // Set up activity listeners
  useEffect(() => {
    // Don't set up listeners if callbacks are not provided (user not logged in)
    if (!onLogout || !onWarning) {
      return;
    }

    // Mouse events
    const mouseEvents = ['mousedown', 'mousemove', 'mouseup', 'click', 'contextmenu'];
    // Keyboard events
    const keyboardEvents = ['keydown', 'keyup', 'keypress'];
    // Scroll events
    const scrollEvents = ['scroll', 'wheel', 'touchmove'];
    // Focus events (navigation)
    const focusEvents = ['focus', 'blur'];

    // Add all event listeners
    const events = [...mouseEvents, ...keyboardEvents, ...scrollEvents, ...focusEvents];
    
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Track form activity
    const handleFormActivity = (event) => {
      const form = event.target.closest('form');
      if (form) {
        form.dataset.recentActivity = 'true';
        setTimeout(() => {
          if (form.dataset) {
            form.dataset.recentActivity = 'false';
          }
        }, 30000); // 30 seconds
      }
      handleActivity();
    };

    document.addEventListener('input', handleFormActivity, { passive: true });
    document.addEventListener('change', handleFormActivity, { passive: true });

    // Track video play/pause
    const handleVideoActivity = () => {
      handleActivity();
    };

    const videos = document.querySelectorAll('video');
    videos.forEach(video => {
      video.addEventListener('play', handleVideoActivity);
      video.addEventListener('pause', handleVideoActivity);
    });

    // Periodic check for inactivity
    const intervalId = setInterval(checkInactivity, checkInterval);

    // Initial activity reset
    resetActivity();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('input', handleFormActivity);
      document.removeEventListener('change', handleFormActivity);
      
      videos.forEach(video => {
        video.removeEventListener('play', handleVideoActivity);
        video.removeEventListener('pause', handleVideoActivity);
      });

      clearInterval(intervalId);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, [handleActivity, checkInactivity, resetActivity, checkInterval, onLogout, onWarning]);

  // Reset on route change (navigation counts as activity)
  useEffect(() => {
    resetActivity();
  }, [location.pathname, resetActivity]);

  // Expose a function to mark logout as triggered (for when modal triggers it)
  const triggerLogout = useCallback(() => {
    logoutTriggeredRef.current = true;
    if (onLogout) {
      onLogout();
    }
  }, [onLogout]);

  return {
    resetActivity,
    isActive: isActiveRef.current,
    triggerLogout, // Allow external components to trigger logout
  };
};

export default useAutoLogout;

